// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import { useEffect, useState, useRef } from 'react';
import { API, Logger, graphqlOperation } from 'aws-amplify';

import useAppContext from '../contexts/app';

import listCallDateShard from '../graphql/queries/listCallDateShard';
import listCallDateHour from '../graphql/queries/listCallDateHour';
import listCalls from '../graphql/queries/listCalls';
import getCall from '../graphql/queries/getCall';

import onCreateCall from '../graphql/queries/onCreateCall';
import onAddTranscriptSegment from '../graphql/queries/onAddTranscriptSegment';
import onUpdateCall from '../graphql/queries/onUpdateCall';
import onShareMeetings from '../graphql/queries/onShareMeetings';
import onDeleteCall from '../graphql/queries/onDeleteCall';
import onUnshareCall from '../graphql/queries/onUnshareCall';
import getTranscriptSegments from '../graphql/queries/getTranscriptSegments';

import { CALL_LIST_SHARDS_PER_DAY } from '../components/call-list/calls-table-config';

const logger = new Logger('useCallsGraphQlApi');
const MEETING_RETENTION_PERIOD_DAYS = process.env.REACT_APP_MEETING_RECORD_EXPIRATION_IN_DAYS;

const useCallsGraphQlApi = ({ initialPeriodsToLoad = CALL_LIST_SHARDS_PER_DAY * 2 } = {}) => {
  const [periodsToLoad, setPeriodsToLoad] = useState(initialPeriodsToLoad);
  const [isCallsListLoading, setIsCallsListLoading] = useState(false);
  const [calls, setCalls] = useState([]);
  const [liveTranscriptCallId, setLiveTranscriptCallId] = useState();
  const [callTranscriptPerCallId, setCallTranscriptPerCallId] = useState({});
  const { setErrorMessage } = useAppContext();
  
  // Constant for subscription reconnection delay (in milliseconds)
  const SUBSCRIPTION_RECONNECT_DELAY = 2000; // 2 seconds
  // Maximum number of reconnection attempts before giving up
  const MAX_RECONNECTION_ATTEMPTS = 3
  ;
  // Flag to track if a subscription error banner has been shown (using ref for persistence across renders)
  const hasShownSubscriptionErrorBannerRef = useRef(false);
  
  // Helper function to handle GraphQL subscriptions with auto-reconnect
  const createSubscriptionWithReconnect = (
    subscriptionName,
    query,
    nextHandler,
    variables = null
  ) => {
    logger.debug(`Creating ${subscriptionName} subscription`);
    let subscription;
    let reconnectAttempts = 0;
    
    const subscribe = () => {
      logger.debug(`Subscribing to ${subscriptionName}`);
      const operation = variables
        ? graphqlOperation(query, variables)
        : graphqlOperation(query);
        
      return API.graphql(operation).subscribe({
        next: nextHandler,
        error: (error) => {
          // Log detailed error information
          logger.warn(`${subscriptionName} error:`, error);
          
          // Unsubscribe from current subscription if it exists
          if (subscription && subscription.unsubscribe) {
            subscription.unsubscribe();
          }
          
          // Check if we've exceeded the maximum reconnection attempts
          if (reconnectAttempts >= MAX_RECONNECTION_ATTEMPTS) {
            logger.error(`Failed to reconnect ${subscriptionName} subscription after ${MAX_RECONNECTION_ATTEMPTS} attempts`);
            
            // Only show the error banner once for any subscription failure
            if (!hasShownSubscriptionErrorBannerRef.current) {
              setErrorMessage("Please refresh the page.");
              hasShownSubscriptionErrorBannerRef.current = true;
            }
            return;
          }
          
          // Increment reconnection attempts counter
          reconnectAttempts += 1;
          
          // Attempt to reconnect on error
          logger.debug(`Error detected, attempting to reconnect ${subscriptionName} subscription (attempt ${reconnectAttempts}/${MAX_RECONNECTION_ATTEMPTS})`);
          
          // Reconnect after the configured delay
          setTimeout(() => {
            logger.debug(`Attempting to reconnect ${subscriptionName} subscription after ${SUBSCRIPTION_RECONNECT_DELAY}ms delay`);
            subscription = subscribe();
          }, SUBSCRIPTION_RECONNECT_DELAY);
        },
      });
    };
    
    subscription = subscribe();
    
    return {
      subscription,
      unsubscribe: () => {
        if (subscription && subscription.unsubscribe) {
          subscription.unsubscribe();
        }
      }
    };
  };

  const setCallsDeduped = (callValues) => {
    setCalls((currentCalls) => {
      const callValuesCallIds = callValues.map((c) => c.CallId);
      return [
        ...currentCalls.filter((c) => !callValuesCallIds.includes(c.CallId)),
        ...callValues.map((call) => ({
          ...call,
          ListPK: call.ListPK || currentCalls.find((c) => c.CallId === call.CallId)?.ListPK,
          ListSK: call.ListSK || currentCalls.find((c) => c.CallId === call.CallId)?.ListSK,
        })),
      ];
    });
  };

  const getCallDetailsFromCallIds = async (callIds) => {
    // prettier-ignore
    const getCallPromises = callIds.map((callId) => (
      API.graphql({ query: getCall, variables: { callId } })
    ));
    const getCallResolutions = await Promise.allSettled(getCallPromises);
    const getCallRejected = getCallResolutions.filter((r) => r.status === 'rejected');
    if (getCallRejected.length) {
      setErrorMessage('failed to get call details - please try again later');
      logger.error('get call promises rejected', getCallRejected);
    }
    const callValues = getCallResolutions.filter((r) => r.status === 'fulfilled').map((r) => r.value?.data?.getCall);

    return callValues;
  };

  useEffect(() => {
    const handleCreateCallUpdate = async ({ provider, value }) => {
      logger.debug('call list subscription update', { provider, value });
      const callId = value?.data?.onCreateCall.CallId || '';
      if (callId) {
        const callValues = await getCallDetailsFromCallIds([callId]);
        setCallsDeduped(callValues);
      }
    };
    
    const { unsubscribe } = createSubscriptionWithReconnect(
      'onCreateCall',
      onCreateCall,
      handleCreateCallUpdate
    );

    return unsubscribe;
  }, []);

  useEffect(() => {
    const handleUpdateCallUpdate = async ({ provider, value }) => {
      logger.debug('call update', { provider, value });
      const callUpdateEvent = value?.data?.onUpdateCall;
      if (callUpdateEvent?.CallId) {
        setCallsDeduped([callUpdateEvent]);
      }
    };
    
    const { unsubscribe } = createSubscriptionWithReconnect(
      'onUpdateCall',
      onUpdateCall,
      handleUpdateCallUpdate
    );

    return unsubscribe;
  }, []);

  useEffect(() => {
    const handleShareMeetingsUpdate = async ({ provider, value }) => {
      logger.debug('share meetings subscription update', { provider, value });
      const sharedCalls = value?.data?.onShareMeetings.Calls || '';
      if (sharedCalls && sharedCalls.length > 0) {
        const callValues = await getCallDetailsFromCallIds(sharedCalls);
        setCallsDeduped(callValues);
      }
    };
    
    const { unsubscribe } = createSubscriptionWithReconnect(
      'onShareMeetings',
      onShareMeetings,
      handleShareMeetingsUpdate
    );

    return unsubscribe;
  }, []);

  useEffect(() => {
    const handleDeleteCallUpdate = async ({ provider, value }) => {
      logger.debug('call delete subscription update', { provider, value });
      const callId = value?.data?.onDeleteCall.CallId || '';
      if (callId) {
        setCalls((currentCalls) => currentCalls.filter((c) => c.CallId !== callId));
      }
    };
    
    const { unsubscribe } = createSubscriptionWithReconnect(
      'onDeleteCall',
      onDeleteCall,
      handleDeleteCallUpdate
    );

    return unsubscribe;
  }, []);

  useEffect(() => {
    const handleUnshareCallUpdate = async ({ provider, value }) => {
      logger.debug('call unshare subscription update', { provider, value });
      const callId = value?.data?.onUnshareCall.CallId || '';
      if (callId) {
        setCalls((currentCalls) => currentCalls.filter((c) => c.CallId !== callId));
      }
    };
    
    const { unsubscribe } = createSubscriptionWithReconnect(
      'onUnshareCall',
      onUnshareCall,
      handleUnshareCallUpdate
    );

    return unsubscribe;
  }, []);

  const handleCallTranscriptSegmentMessage = (transcriptSegment) => {
    const { callId, transcript, isPartial, channel } = transcriptSegment;

    setCallTranscriptPerCallId((current) => {
      logger.debug('setCallTrancriptPerCallId current: ', current);

      const currentContactEntry = current[callId] || {};
      const currentChannelEntry = currentContactEntry[channel] || {};

      const currentBase = currentChannelEntry?.base || '';
      const currentSegments = currentChannelEntry?.segments || [];
      logger.debug('setCallTrancriptPerCallId current segments: ', currentSegments);
      const lastSameSegmentId = currentSegments.filter((s) => s.segmentId === transcriptSegment.segmentId).pop();
      const dedupedSegments = currentSegments.filter((s) => s.segmentId !== transcriptSegment.segmentId);

      const segments = [
        ...dedupedSegments,
        // prettier-ignore
        // avoid overwriting a final segment or one with sentiment with a late arriving segment
        (lastSameSegmentId?.isPartial === false && transcriptSegment?.isPartial === true)
        || (lastSameSegmentId?.isPartial === false && lastSameSegmentId?.sentiment)
          ? lastSameSegmentId
          : transcriptSegment,
      ].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

      const entry = {
        ...currentContactEntry,
        [channel]: {
          base: !isPartial ? `${currentBase} ${transcript}`.trim() : currentBase,
          lastPartial: isPartial ? transcript : '',
          segments,
        },
      };
      logger.debug('setCallTrancriptPerCallId new contact id entry: ', entry);

      return {
        ...current,
        [callId]: { ...entry },
      };
    });
  };

  const mapTranscriptSegmentValue = (transcriptSegmentValue) => {
    const {
      CallId: callId,
      SegmentId: segmentId,
      StartTime: startTime,
      EndTime: endTime,
      Speaker: speaker,
      Transcript: transcript,
      IsPartial: isPartial,
      Channel: channel,
      CreatedAt: createdAt,
      Sentiment: sentiment,
      SentimentScore: sentimentScore,
      SentimentWeighted: sentimentWeighted,
      Annotation: annotation,
      TranscriptEdited: edited,
    } = transcriptSegmentValue;

    return {
      callId,
      segmentId,
      startTime,
      endTime,
      speaker,
      transcript,
      isPartial,
      channel,
      createdAt,
      sentiment,
      sentimentScore,
      sentimentWeighted,
      annotation,
      edited,
    };
  };

  useEffect(() => {
    logger.debug('onAddTranscriptSegment effect');

    if (!liveTranscriptCallId) {
      // No active transcript call ID, nothing to subscribe to
      return () => {};
    }
    
    const handleTranscriptSegmentUpdate = async ({ provider, value }) => {
      logger.debug('call transcript subscription update', { provider, value });
      const transcriptSegmentValue = value?.data?.onAddTranscriptSegment;
      if (!transcriptSegmentValue) {
        return;
      }
      const transcriptSegment = mapTranscriptSegmentValue(transcriptSegmentValue);
      const { callId, transcript, segmentId } = transcriptSegment;
      if (callId !== liveTranscriptCallId) {
        return;
      }
      if (transcript && segmentId) {
        handleCallTranscriptSegmentMessage(transcriptSegment);
      }
    };
    
    const { unsubscribe } = createSubscriptionWithReconnect(
      'onAddTranscriptSegment',
      onAddTranscriptSegment,
      handleTranscriptSegmentUpdate,
      { callId: liveTranscriptCallId }
    );

    return () => {
      logger.debug('unsubscribed from transcript segments');
      unsubscribe();
    };
  }, [liveTranscriptCallId]);

  const listCallIdsByDateShards = async ({ date, shards }) => {
    const listCallDateShardPromises = shards.map((i) => {
      logger.debug('sending list call date shard', date, i);
      return API.graphql({ query: listCallDateShard, variables: { date, shard: i } });
    });
    const listCallDateShardResolutions = await Promise.allSettled(listCallDateShardPromises);

    const listRejected = listCallDateShardResolutions.filter((r) => r.status === 'rejected');
    if (listRejected.length) {
      setErrorMessage('failed to list calls - please try again later');
      logger.error('list call promises rejected', listRejected);
    }
    const callData = listCallDateShardResolutions
      .filter((r) => r.status === 'fulfilled')
      .map((r) => r.value?.data?.listCallsDateShard?.Calls || [])
      .reduce((pv, cv) => [...cv, ...pv], []);

    return callData;
  };

  const listCallIdsByDateHours = async ({ date, hours }) => {
    const listCallDateHourPromises = hours.map((i) => {
      logger.debug('sending list call date hour', date, i);
      return API.graphql({ query: listCallDateHour, variables: { date, hour: i } });
    });
    const listCallDateHourResolutions = await Promise.allSettled(listCallDateHourPromises);

    const listRejected = listCallDateHourResolutions.filter((r) => r.status === 'rejected');
    if (listRejected.length) {
      setErrorMessage('failed to list calls - please try again later');
      logger.error('list call promises rejected', listRejected);
    }

    const callData = listCallDateHourResolutions
      .filter((r) => r.status === 'fulfilled')
      .map((r) => r.value?.data?.listCallsDateHour?.Calls || [])
      .reduce((pv, cv) => [...cv, ...pv], []);

    return callData;
  };

  const listCallIds = async () => {
    // this uses a Scan of dynamoDB - prefer using the shard based queries
    const now = new Date(new Date().toLocaleString('en', { timeZone: 'UTC' }));
    
    // Calculate startDateTime using setUTCDate to be consistent with endDateTime calculation
    const startDateTime = new Date(now);
    startDateTime.setUTCDate(now.getUTCDate() - MEETING_RETENTION_PERIOD_DAYS);

    // Use +1 day for endTime, since in DynamoDB Scan result, it will not include "cls#2025-08-07#s#00"
    // if providing "cls#2025-08-07" as string input. Extend to use "cls#2025-08-08" works
    const endDateTime = new Date(now);
    endDateTime.setUTCDate(now.getUTCDate() + 1);

    console.log('List calls between (UTC): ', startDateTime.toUTCString(), endDateTime.toUTCString());

    // Helper function to fetch a page of results
    const fetchPage = async (token) => {
      const response = await API.graphql({
        query: listCalls,
        variables: {
          endDateTime,
          startDateTime,
          nextToken: token,
        },
      });

      const listCallsResponse = response?.data?.listCalls?.Calls || [];
      const nextToken = response?.data?.listCalls?.nextToken;

      if (nextToken) {
        // If there's a next token, fetch the next page and combine results
        const nextPageCalls = await fetchPage(nextToken);
        return [...listCallsResponse, ...nextPageCalls];
      }

      // No more pages, return current results
      return listCallsResponse;
    };

    try {
      // Start fetching from the first page
      const allCalls = await fetchPage(null);
      
      // Extract and filter CallIds
      return allCalls
        .map(item => item?.CallId)
        .filter(Boolean);
    } catch (error) {
      setErrorMessage('failed to list calls - please try again later');
      logger.error('Error fetching calls', error);
      return [];
    }
  };

  const sendSetCallsForPeriod = async () => {
    // XXX this logic should be moved to the API
    try {
      // If periodsToLoad is 0, use listCalls to get all data
      if (periodsToLoad === 0) {
        const callIds = await listCallIds();

        const callDetails = await getCallDetailsFromCallIds(callIds);

        // Since listCallIds() doesn't return PK/SK data, we need to handle this differently
        // than the date shard approach. We'll set ListPK and ListSK to the CallId as a fallback.
        const callValuesWithKeys = callDetails.map(detail => ({
          ...detail,
          ListPK: detail.PK || detail.CallId,
          ListSK: detail.SK || detail.CallId
        }));

        setCallsDeduped(callValuesWithKeys);
        setIsCallsListLoading(false);
        return;
      }

      const now = new Date();

      // array of arrays containing date / shard pairs relative to current UTC time
      // e.g. 2 periods to on load 2021-01-01T:20:00:00.000Z ->
      // [ [ '2021-01-01', 3 ], [ '2021-01-01', 4 ] ]
      const hoursInShard = 24 / CALL_LIST_SHARDS_PER_DAY;
      const dateShardPairs = [...Array(parseInt(periodsToLoad, 10)).keys()].map((p) => {
        const deltaInHours = p * hoursInShard;
        const relativeDate = new Date(now - deltaInHours * 3600 * 1000);

        const relativeDateString = relativeDate.toISOString().split('T')[0];
        const shard = Math.floor(relativeDate.getUTCHours() / hoursInShard);

        return [relativeDateString, shard];
      });

      // reduce array of date/shard pairs into object of shards by date
      // e.g. [ [ '2021-01-01', 3 ], [ '2021-01-01', 4 ] ] -> { '2021-01-01': [ 3, 4 ] }
      const dateShards = dateShardPairs.reduce((p, c) => ({ ...p, [c[0]]: [...(p[c[0]] || []), c[1]] }), {});
      logger.debug('call list date shards', dateShards);

      // parallelizes listCalls and getCallDetails
      // alternatively we could implement it by sending multiple graphql queries in 1 request
      const callDataDateShardPromises = Object.keys(dateShards).map(
        // pretttier-ignore
        async (d) => listCallIdsByDateShards({ date: d, shards: dateShards[d] }),
      );

      // get contact Ids by hour on residual hours outside of the lower shard date/hour boundary
      // or just last n hours when periodsToLoad is less than 1 shard period
      let baseDate;
      let residualHours;
      if (periodsToLoad < 1) {
        baseDate = new Date(now);
        const numHours = parseInt(periodsToLoad * hoursInShard, 10);
        residualHours = [...Array(numHours).keys()].map((h) => baseDate.getUTCHours() - h);
      } else {
        baseDate = new Date(now - periodsToLoad * hoursInShard * 3600 * 1000);
        const residualBaseHour = baseDate.getUTCHours() % hoursInShard;
        residualHours = [...Array(hoursInShard - residualBaseHour).keys()].map((h) => baseDate.getUTCHours() + h);
      }
      const baseDateString = baseDate.toISOString().split('T')[0];

      const residualDateHours = { date: baseDateString, hours: residualHours };
      logger.debug('call list date hours', residualDateHours);

      const callDataDateHourPromise = listCallIdsByDateHours(residualDateHours);

      const callDataPromises = [...callDataDateShardPromises, callDataDateHourPromise];
      const callDetailsPromises = callDataPromises.map(async (callDataPromise) => {
        const callData = await callDataPromise;
        const callIds = callData.map((item) => item.CallId);
        const callDetails = await getCallDetailsFromCallIds(callIds);
        // Merge call details with PK and SK
        return callDetails.map((detail) => {
          const matchingData = callData.find((item) => item.CallId === detail.CallId);
          return { ...detail, ListPK: matchingData.PK, ListSK: matchingData.SK };
        });
      });

      const callValuesPromises = callDetailsPromises.map(async (callValuesPromise) => {
        const callValues = await callValuesPromise;
        logger.debug('callValues', callValues);
        return callValues;
      });

      const getCallsPromiseResolutions = await Promise.allSettled(callValuesPromises);
      logger.debug('getCallsPromiseResolutions', getCallsPromiseResolutions);
      const callValuesReduced = getCallsPromiseResolutions
        .filter((r) => r.status === 'fulfilled')
        .map((r) => r.value)
        .reduce((previous, current) => [...previous, ...current], []);
      logger.debug('callValuesReduced', callValuesReduced);
      setCallsDeduped(callValuesReduced);
      setIsCallsListLoading(false);
      const getCallsRejected = getCallsPromiseResolutions.filter((r) => r.status === 'rejected');
      if (getCallsRejected.length) {
        setErrorMessage('failed to get call details - please try again later');
        logger.error('get call promises rejected', getCallsRejected);
      }
    } catch (error) {
      setIsCallsListLoading(false);
      setErrorMessage('failed to list calls - please try again later');
      logger.error('error obtaining call list', error);
    }
  };

  useEffect(() => {
    if (isCallsListLoading) {
      logger.debug('call list is loading');
      // send in a timeout to avoid blocking rendering
      setTimeout(() => {
        setCalls([]);
        sendSetCallsForPeriod();
      }, 1);
    }
  }, [isCallsListLoading]);

  useEffect(() => {
    logger.debug('list period changed', periodsToLoad);
    setIsCallsListLoading(true);
  }, [periodsToLoad]);

  const fetchAllTranscriptSegments = async (callId, token = null) => {
    const response = await API.graphql({
      query: getTranscriptSegments,
      variables: { callId, nextToken: token },
    });
    const page = response?.data?.getTranscriptSegments;
    const transcriptSegments = page?.TranscriptSegments || [];
    const pageNextToken = page?.nextToken || null;

    if (pageNextToken) {
      const remainingSegments = await fetchAllTranscriptSegments(callId, pageNextToken);
      return [...transcriptSegments, ...remainingSegments];
    }
    return transcriptSegments;
  };

  const sendGetTranscriptSegmentsRequest = async (callId) => {
    try {
      const allTranscriptSegments = await fetchAllTranscriptSegments(callId);

      logger.debug('transcript segments response', allTranscriptSegments);
      if (allTranscriptSegments?.length > 0) {
        const transcriptSegmentsReduced = allTranscriptSegments
          .map((t) => mapTranscriptSegmentValue(t))
          .reduce((p, c) => {
            const previousSegments = p[c.channel]?.segments || [];
            const lastSameSegmentId = previousSegments.filter((s) => s?.segmentId === c?.segmentId).pop();
            const dedupedSegments = previousSegments.filter((s) => s.segmentId !== c.segmentId);

            // prettier-ignore
            const segment = !lastSameSegmentId?.sentiment && c?.sentiment
              ? c
              : lastSameSegmentId || c;

            return { ...p, [c.channel]: { segments: [...dedupedSegments, segment] } };
          }, {});

        setCallTranscriptPerCallId((current) => {
          logger.debug('updating callTranscriptPerCallId', current, transcriptSegmentsReduced);
          return {
            ...current,
            [callId]: transcriptSegmentsReduced,
          };
        });
      }
    } catch (error) {
      setErrorMessage('failed to get transcript - please try again later');
      logger.error('failed to set transcript segments', error);
    }
  };

  return {
    calls,
    callTranscriptPerCallId,
    isCallsListLoading,
    getCallDetailsFromCallIds,
    sendGetTranscriptSegmentsRequest,
    setIsCallsListLoading,
    setLiveTranscriptCallId,
    setPeriodsToLoad,
    periodsToLoad,
  };
};

export default useCallsGraphQlApi;
